<script lang="ts">
	import "@glidejs/glide/dist/css/glide.core.min.css";
	import * as Glide from "@glidejs/glide";
	import logo from "../../../../core/assets/logo.svg";
	import type { ActionReturn } from "svelte/action";
	import type { UploadedFile } from "../../../../core/schema/UploadedFile.js";
	import { disableGlidePerSlideAmount } from "../../utils/disableGlidePerSlideAmount.js";
	import DynamicPicture from "../Picture/DynamicPicture.svelte";
	import StaticPicture from "../Picture/StaticPicture.svelte";
	import { appName } from "../../../../core/schema/names/appName.js";
	import type { Position } from "../../../../core/schema/Position.js";
	import translations from "./CareerMobileGallery.translations.json";
	import { getTranslate } from "../../utils/getTranslate.js";

	export let images: UploadedFile[];
	export let position: Position;
	export let gliderClass = "";
	export { gliderClass as class };

	const translate = getTranslate(translations);

	function glide(element: HTMLDivElement): ActionReturn {
		const glideOptions = {
			type: "carousel",
			startAt: 0,
			focusAt: "center",
			perView: 2.75,
			autoplay: images.length > 1 ? 3000 : 0,
			hoverpause: false,
			animationDuration: 3000,
			animationTimingFunc: "linear",
			gap: 9,
			keyboard: true,
			cloningRatio: 2,
			breakpoints: {
				736: { perView: 2.7 },
				720: { perView: 2.6 },
				670: { perView: 2.35 },
				590: { perView: 2.2 },
				570: { perView: 2.1 },
				540: { perView: 2 },
				515: { perView: 1.85 },
				480: { perView: 1.75 },
				455: { perView: 1.65 },
				420: { perView: 1.5 },
				370: { perView: 1.35 },
				340: { perView: 1.225 },
			},
		} satisfies Partial<Glide.Options>;

		// eslint-disable-next-line new-cap
		const glide = new Glide.default(element, glideOptions).mount();
		disableGlidePerSlideAmount(glide, element);
		glide.on("resize", () => {
			disableGlidePerSlideAmount(glide, element);
		});

		return {
			destroy(): void {
				glide.destroy();
			},
		};
	}
</script>

{#if images.length > 0}
	<div class={gliderClass}>
		<div use:glide class="glide">
			<div class="glide__track" data-glide-el="track">
				<div class="glide__slides">
					{#each images as image, i}
						<DynamicPicture
							class="glide__slide aspect-square !h-[22rem] rounded-lg bg-white"
							imgClass="max-w-full h-full w-auto rounded-lg aspect-square object-cover"
							{image}
							lightboxId={position.name}
							lightboxWidth={400}
							width={600}
							height={300}
							bucket="storage"
							loading="eager"
							alt="{translate('photo')} – {position.name} ({i + 1}/{images.length})"
						/>
					{/each}
				</div>
				<div class="flex justify-center">
					<div class="glide__bullets bg-ltsecondaryBlack-150 mt-5 flex h-0.5 w-10/12" data-glide-el="controls[nav]">
						{#each images as _image, i}
							<span
								class="glide__bullet text-ltsecondaryBlack hover:bg-ltsecondaryBlack-300 w-full cursor-pointer transition-all duration-200"
								data-glide-dir="={i}"
							></span>
						{/each}
					</div>
				</div>
			</div>
		</div>
	</div>
{:else}
	<div class="flex justify-center">
		<div
			class="group flex aspect-square h-[17rem] w-[90%] items-center justify-center rounded-lg bg-white p-4 md:hidden"
		>
			<StaticPicture
				alt="Logo {appName}"
				image={logo}
				class="w-24 opacity-50 transition-all duration-300 group-hover:opacity-100"
				loading="eager"
				width={96}
				height={96}
			/>
		</div>
	</div>
{/if}
